// @flow @jsx h

import { Component, h } from 'preact'
import { connect } from '../../lib/provider'
import styles from './App.css'
import { debounce } from '../../lib/debounce'
import { bem } from '../../lib/bem'

import { InfoBox } from './InfoBox'
import { Trash } from './Trash'
import { Sea } from './Sea'
import { SeaOverlay } from './SeaOverlay'
import { SeaLight } from './SeaLight'

const PLASTIC_IN_KILO_PER_SECOND = 250
const MAX_FPS_COUNT = 3
let trackFpsCount = 0

type Props = {
  href?: string,
  showLink?: string,
  linkText?: string,
  sendErrorReport: Function,
  trackAction: Function,
  isXl?: boolean
}

type State = {
  amount: number,
  hasIntersected: boolean,
  prefersReducedMotion: boolean,
  speedModifier: number,
  breakpoint: string,
  width: number,
  height: number
}

class AppComponent extends Component<Props, State> {
  state: State = {
    amount: 0,
    hasIntersected: false,
    prefersReducedMotion: false,
    speedModifier: 2.5,
    breakpoint: 'small',
    width: 0,
    height: 0
  }

  interval: any = null // IntervalID
  observer: any = null
  debouncedResize: ?() => void = null
  rootEl: ?HTMLElement = null

  componentDidMount (): void {
    this.handleResize()
    this.debouncedResize = debounce(this.handleResize, 16.667)
    window.addEventListener('resize', this.debouncedResize)

    this.handleReducedMotion()

    this.observer = new IntersectionObserver(this.handleObserve, {})
    const { rootEl } = this
    if (rootEl) {
      this.setState({ width: rootEl.offsetWidth, height: rootEl.offsetHeight })
      this.observer.observe(rootEl)
    }

    this.interval = setInterval(() => {
      const { amount } = this.state
      this.setState({
        amount: amount + PLASTIC_IN_KILO_PER_SECOND / this.state.speedModifier
      })
    }, 1000 / this.state.speedModifier)

    if (this.props.isXl) {
      this.overrideXlStyles()
    }
  }

  overrideXlStyles = () => {
    const allContainers = [
      ...document.querySelectorAll('.remoterenderedcontent-reference')
    ]

    const container = allContainers.filter(c =>
      c.querySelector(`.${styles.app}`)
    )[0]

    if (container) {
      container.style.padding = '0'
    }

    if (this.rootEl) {
      this.setState({
        width: this.rootEl.offsetWidth,
        height: this.rootEl.offsetHeight
      })
    }
  }

  componentWillUnmount (): void {
    window.removeEventListener('resize', this.debouncedResize)
    if (this.interval) {
      clearInterval(this.interval)
    }
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }

  handleReducedMotion = () => {
    const { trackAction } = this.props
    const { matches: prefersReducedMotion } = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    )
    this.setState({ prefersReducedMotion })
    if (prefersReducedMotion) {
      this.setState({ speedModifier: 0.5 })
      trackAction(`PREFER_REDUCED_MOTION`)
    }
  }

  handleFpsUpdate = (fps) => {
    const { trackAction } = this.props
    if (trackFpsCount <= MAX_FPS_COUNT) {
      if (fps <= 61) {
        trackAction('FPS', fps)
        trackFpsCount++
      }
    }
  }

  handleObserve = (enteries, observer) => {
    const { isIntersecting } = (enteries && enteries[0]) || false
    const { hasIntersected } = this.state
    const { trackAction } = this.props

    if (isIntersecting && !hasIntersected) {
      this.setState({ hasIntersected: true })
      trackAction('SCROLL_IN')
    } else if (hasIntersected) {
      trackAction(isIntersecting ? 'SCROLL_IN' : 'SCROLL_OUT')
    }
  }

  handleResize = () => {
    if (!this.rootEl) return
    this.setState({ breakpoint: getCurrentBreakpoint(this.rootEl.offsetWidth) })
  }

  render () {
    const { href, showLink, linkText } = this.props
    const {
      amount,
      hasIntersected,
      breakpoint,
      height,
      width,
      prefersReducedMotion
    } = this.state
    return (
      <div
        class={bem(styles.app, breakpoint, hasIntersected && 'has-intersected')}
        ref={el => {
          this.rootEl = el
        }}
      >
        <Sea />
        <Trash
          prefersReducedMotion={prefersReducedMotion}
          shouldMount={hasIntersected}
          height={height}
          width={width}
          onFpsUpdate={this.handleFpsUpdate}
        />
        <SeaLight />
        <SeaOverlay />
        <InfoBox
          amount={amount}
          showLink={showLink}
          href={href}
          linkText={linkText}
        />
      </div>
    )
  }
}

const App = connect()(AppComponent)

export { App }

function getCurrentBreakpoint (width) {
  if (width > 1600) return 'extra-large'
  if (width > 1200) return 'large'
  if (width > 800) return 'medium'
  return 'small'
}



// WEBPACK FOOTER //
// ./src/v1/components/App.js